import React from 'react';
import {
  Sparkles, Calendar, BadgeEuro, ShieldCheck, BarChart3, MessageSquare,
  Bell, Wand2, FileCheck2, Heart, Lock, Instagram, Smartphone, Gavel,
  PoundSterling,
} from 'lucide-react';

/**
 * Market-aware mini-mockups. Each accepts a `market` object (the object
 * returned from `useMarket()`) so the same visual can render with £, $ or €
 * pricing, UK/US/IE energy-rating badges, and locally-plausible neighbourhood
 * references. Sub-90px SVG/Tailwind — no external assets.
 *
 * Callers (e.g. FeatureShowcaseGrid) construct market copy at the parent
 * level, then hand each mockup only the numeric/label slots it needs.
 */

/* ── Market copy helpers ───────────────────────────────────────────────── */

/**
 * Returns market-specific labels used across the mockups. Keeps every
 * substitution centralised so a future USA / DE / FR launch only needs one
 * new branch here rather than 20 hand-touched lines in individual mockups.
 */
export const marketMockupCopy = (market) => {
  const isUK = market.code === 'GB';
  const isUS = market.code === 'US';
  const fmt = (n) => market.formatCurrency(n);
  // Short-form price (£482k / €482k / $482k) for the tight mockup badges.
  const fmtShort = (n) => {
    const symbol = market.currency_symbol || '€';
    const k = Math.round(n / 1000);
    return `${symbol}${k.toLocaleString(market.locale || 'en-IE')}k`;
  };
  return {
    isUK,
    isUS,
    fmt,
    fmtShort,
    currencySymbol: market.currency_symbol || '€',
    // Neighbourhood used for card copy — a well-known area in each market.
    exampleArea: isUK ? 'Camden' : isUS ? 'Brooklyn Heights' : 'Rathmines',
    // Transit shorthand referenced in "walk to X" euphemism reveal
    exampleTransit: isUK ? 'Tube' : isUS ? 'subway' : 'Luas',
    // Energy rating label ("BER" IE, "EPC" UK, "Home Energy Score" US)
    energyLabel: isUK ? 'EPC' : isUS ? 'Home Energy Score' : 'BER',
    // Public sold-price register
    registerLabel: isUK ? 'HM Land Registry' : isUS ? 'Public records' : 'PPR',
    registerRegulator: isUK ? 'Land Registry' : isUS ? 'MLS' : 'SEAI',
    // Postcode label
    postcodeLabel: isUK ? 'postcode' : isUS ? 'ZIP code' : 'Eircode',
    // Example B-grade energy rating shown in the BER pill
    energyGrade: isUK ? 'C' : isUS ? '7' : 'B2',
    // Currency icon component for the offer thread badge
    CurrencyIcon: isUK || isUS ? PoundSterling : BadgeEuro,
  };
};

/* ── AGENT side ─────────────────────────────────────────────────────────── */

export const AISuggestReplyMockup = () => (
  <div className="w-full">
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2.5 mb-2 max-w-[88%]">
      <div className="text-[10px] text-gray-400">Buyer · 2 min</div>
      <div className="text-[11.5px] text-gray-800 leading-snug">
        Is it still available? When can I view?
      </div>
    </div>
    <div className="flex flex-wrap gap-1.5 ml-1">
      <span className="px-2 py-1 rounded-full bg-[#7042cc] text-white text-[10px] font-semibold inline-flex items-center gap-1">
        <Sparkles className="h-2.5 w-2.5" />
        Yes — Sat 10am works?
      </span>
      <span className="px-2 py-1 rounded-full bg-white text-[#5b35a8] ring-1 ring-[#7042cc]/30 text-[10px] font-semibold">
        Sun 11–12?
      </span>
    </div>
  </div>
);

export const OfferThreadMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="w-full max-w-[260px] mx-auto">
      <div className="rounded-lg bg-emerald-50 ring-1 ring-emerald-200 p-2.5 mb-1.5">
        <div className="flex items-center justify-between mb-1">
          <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">
            <c.CurrencyIcon className="h-3 w-3" /> Offer
          </span>
          <span className="text-[10px] text-emerald-700 font-semibold">Pending</span>
        </div>
        <div className="text-[16px] font-bold text-gray-900 leading-none">{c.fmt(482500)}</div>
        <div className="text-[10px] text-gray-600 mt-0.5">Subject to survey · 6-week close</div>
      </div>
      <div className="flex gap-1.5">
        <button className="flex-1 text-[10px] font-semibold py-1 rounded bg-emerald-600 text-white">Accept</button>
        <button className="flex-1 text-[10px] font-semibold py-1 rounded bg-white ring-1 ring-gray-300 text-gray-700">Counter</button>
      </div>
    </div>
  );
};

export const ViewingBookingMockup = () => (
  <div className="w-full max-w-[260px] mx-auto">
    <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 p-2.5">
      <div className="flex items-center gap-1.5 mb-1.5 text-amber-700 text-[10px] font-bold uppercase tracking-wide">
        <Calendar className="h-3 w-3" /> Viewing proposed
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[11px] bg-white rounded px-2 py-1 ring-1 ring-amber-100">
          <span className="text-gray-700">Sat · 10:00</span>
          <span className="text-amber-700 font-semibold">Pick</span>
        </div>
        <div className="flex items-center justify-between text-[11px] bg-white rounded px-2 py-1 ring-1 ring-amber-100">
          <span className="text-gray-700">Sun · 13:00</span>
          <span className="text-amber-700 font-semibold">Pick</span>
        </div>
      </div>
      <div className="text-[9px] text-gray-500 mt-1.5">+ Add to Calendar (.ics)</div>
    </div>
  </div>
);

export const ShareCardMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="relative w-full flex items-center justify-center">
      <div className="rounded-md overflow-hidden ring-1 ring-black/10 shadow w-[200px] h-[105px] bg-gradient-to-br from-[#361c6e] to-[#0f0a1f] flex">
        <div className="flex-1 p-2 flex flex-col justify-between">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm bg-[#7042cc]" />
            <span className="text-white text-[8px] font-bold">lovinahome</span>
          </div>
          <div>
            <span className="text-[8px] text-white bg-[#ff84b2] rounded-full px-1.5 py-0.5 inline-block mb-1 font-bold">For Sale</span>
            <div className="text-white text-[11px] font-extrabold leading-none">{c.fmt(695000)}</div>
            <div className="text-white text-[7px] opacity-80 leading-tight mt-0.5">3 Bed · {c.exampleArea}</div>
          </div>
        </div>
        <div className="w-[70px] bg-[url('https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=320')] bg-cover bg-center" />
      </div>
      <div className="absolute -bottom-1.5 -right-2 w-7 h-7 rounded-full bg-gradient-to-br from-[#feda75] via-[#fa7e1e] to-[#d62976] flex items-center justify-center ring-2 ring-white">
        <Instagram className="w-3.5 h-3.5 text-white" />
      </div>
    </div>
  );
};

export const EncryptedChatMockup = () => (
  <div className="w-full max-w-[240px] mx-auto space-y-1.5">
    <div className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700 mb-0.5">
      <Lock className="h-3 w-3" />
      End-to-end · AES-256
    </div>
    <div className="ml-auto bg-[#7042cc] text-white text-[11px] rounded-lg rounded-tr-sm px-2 py-1 max-w-[70%]">
      Could we view at 4pm?
    </div>
    <div className="bg-gray-100 text-gray-800 text-[11px] rounded-lg rounded-tl-sm px-2 py-1 max-w-[70%]">
      Yes — sending an offer now.
    </div>
  </div>
);

export const VerifiedBerMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="w-full flex flex-col items-center gap-1.5">
      <div className="flex items-center gap-1.5">
        <div className="w-9 h-9 rounded-md bg-emerald-500 text-white font-extrabold flex items-center justify-center text-base shadow">
          {c.energyGrade}
        </div>
        <div className="text-left">
          <div className="text-[10px] font-bold text-gray-900">{c.energyLabel} verified</div>
          <div className="text-[9px] text-gray-500">Cross-checked with {c.registerRegulator}</div>
        </div>
        <ShieldCheck className="h-5 w-5 text-emerald-600 ml-1" />
      </div>
      <div className="text-[10px] text-gray-500 italic">&quot;No marketing claims — the real rating.&quot;</div>
    </div>
  );
};

export const PprPriceMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="w-full max-w-[230px] mx-auto">
      <div className="text-[10px] text-gray-500 mb-1">
        Median sold · {c.exampleArea} · last 12m
      </div>
      <div className="flex items-end gap-1.5 h-12">
        {[35, 50, 42, 65, 60, 78].map((h, i) => (
          <div
            key={i}
            className={`flex-1 rounded-sm ${i === 5 ? 'bg-[#7042cc]' : 'bg-[#7042cc]/30'}`}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-gray-600">{c.fmtShort(612000)} median</span>
        <span className="text-[10px] font-semibold text-emerald-700">↑ 4.8% YoY</span>
      </div>
    </div>
  );
};

export const PassportMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="w-full max-w-[200px] mx-auto rounded-lg bg-white ring-1 ring-indigo-200 p-2.5 relative">
      <div className="flex items-center gap-1.5 mb-1.5">
        <FileCheck2 className="h-4 w-4 text-indigo-600" />
        <span className="text-[10px] font-bold text-gray-900">Property Passport</span>
      </div>
      <div className="space-y-1 text-[10px] text-gray-600">
        <div className="flex justify-between"><span>{c.energyLabel} cert</span><span className="text-emerald-600 font-bold">✓</span></div>
        <div className="flex justify-between"><span>Title deed</span><span className="text-emerald-600 font-bold">✓</span></div>
        <div className="flex justify-between"><span>e-signature</span><span className="text-emerald-600 font-bold">✓</span></div>
      </div>
      <div className="absolute -top-1.5 -right-1.5 rotate-[14deg] bg-indigo-600 text-white text-[8px] font-extrabold px-1.5 py-0.5 rounded">eIDAS</div>
    </div>
  );
};

/* ── BUYER side ─────────────────────────────────────────────────────────── */

export const HonestReadMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="w-full max-w-[230px] mx-auto rounded-lg bg-white ring-1 ring-rose-200 p-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="inline-flex items-center gap-1 text-rose-700 text-[10px] font-bold uppercase tracking-wide">
          <Wand2 className="h-3 w-3" /> Honest Read
        </span>
        <span className="text-[10px] font-bold text-amber-700">3.6 / 5</span>
      </div>
      <div className="text-[10.5px] text-gray-700 leading-snug italic">
        &quot;Listing says <span className="line-through">&apos;walk to {c.exampleTransit}&apos;</span> — it&apos;s 22 minutes. Garden faces north, not &apos;sunny&apos;.&quot;
      </div>
    </div>
  );
};

export const NegotiationMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="w-full max-w-[230px] mx-auto rounded-lg bg-white ring-1 ring-amber-200 p-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="inline-flex items-center gap-1 text-amber-700 text-[10px] font-bold uppercase tracking-wide">
          <BarChart3 className="h-3 w-3" /> Negotiation IQ
        </span>
      </div>
      <div className="text-[10.5px] text-gray-700 leading-snug">
        Sold for <span className="line-through text-gray-400">{c.fmtShort(485000)}</span> 23 days ago next door.
        Open with <span className="font-bold text-amber-700">{c.fmtShort(462000)}</span> — ceiling {c.fmtShort(478000)}.
      </div>
    </div>
  );
};

export const PriceAlertMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="w-full max-w-[210px] mx-auto">
      <div className="rounded-2xl bg-white shadow-md ring-1 ring-black/10 p-2.5 flex gap-2 items-start">
        <div className="w-7 h-7 rounded-lg bg-[#7042cc] flex items-center justify-center shrink-0">
          <Bell className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="flex-1 text-left">
          <div className="text-[10px] text-gray-400">lovinahome · now</div>
          <div className="text-[11.5px] font-semibold text-gray-900 leading-tight">Price dropped {c.fmtShort(15000)}</div>
          <div className="text-[10px] text-gray-600">Saved search: {c.exampleArea} · 3 bed</div>
        </div>
      </div>
    </div>
  );
};

export const DesignRoomMockup = () => (
  <div className="w-full max-w-[220px] mx-auto rounded-lg overflow-hidden ring-1 ring-fuchsia-200 grid grid-cols-2 h-20">
    <div className="bg-[url('https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=240')] bg-cover bg-center relative">
      <span className="absolute bottom-1 left-1 text-[8px] bg-black/60 text-white px-1.5 py-0.5 rounded">Before</span>
    </div>
    <div className="bg-[url('https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=240')] bg-cover bg-center relative">
      <span className="absolute bottom-1 left-1 text-[8px] bg-fuchsia-600 text-white px-1.5 py-0.5 rounded font-semibold">Scandi</span>
    </div>
  </div>
);

export const PwaInstallMockup = () => (
  <div className="w-full flex items-center justify-center gap-3">
    <div className="relative">
      <div className="w-[60px] h-[100px] rounded-[12px] bg-gray-900 ring-1 ring-gray-800 p-1.5 shadow">
        <div className="w-full h-full rounded-md bg-gradient-to-br from-[#5b35a8] to-[#0f0a1f] flex items-center justify-center">
          <Heart className="h-5 w-5 text-white fill-white" />
        </div>
      </div>
      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 ring-2 ring-white flex items-center justify-center">
        <Smartphone className="h-2.5 w-2.5 text-white" />
      </div>
    </div>
    <div className="text-left">
      <div className="text-[10px] font-bold text-gray-900">Install lovinahome</div>
      <div className="text-[10px] text-gray-500">Works offline · Lock-screen push</div>
    </div>
  </div>
);

export const DirectEnquiryMockup = () => (
  <div className="w-full max-w-[220px] mx-auto rounded-lg bg-white ring-1 ring-emerald-200 p-2.5">
    <div className="flex items-center gap-1.5 mb-1 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">
      <MessageSquare className="h-3 w-3" /> Direct enquiry · No middleman
    </div>
    <div className="text-[10.5px] text-gray-700">
      Buyer → <span className="font-semibold">you</span> · zero funnel · zero lead-resale.
    </div>
    <div className="mt-1.5 text-[10px] text-gray-500 italic">
      Avg. response time: <span className="font-semibold text-emerald-700">8 min</span>
    </div>
  </div>
);

export const RealPriceIndexMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="w-full max-w-[230px] mx-auto rounded-lg bg-white ring-1 ring-sky-200 p-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="inline-flex items-center gap-1 text-sky-700 text-[10px] font-bold uppercase tracking-wide">
          <BarChart3 className="h-3 w-3" /> Real Price Index
        </span>
        <span className="text-[10px] font-bold text-emerald-600">+4.8%</span>
      </div>
      <svg viewBox="0 0 100 28" className="w-full h-7">
        <polyline
          points="0,22 14,18 28,20 42,12 56,15 70,9 84,6 100,4"
          fill="none"
          stroke="#0284c7"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="text-[10px] text-gray-500 mt-1">Live, official {c.registerLabel} data · 200+ areas</div>
    </div>
  );
};

export const StripePromoMockup = () => (
  <div className="w-full max-w-[220px] mx-auto rounded-lg bg-gradient-to-br from-amber-50 to-rose-50 ring-1 ring-amber-200 p-2.5">
    <div className="text-[10px] text-amber-700 font-bold uppercase tracking-wide mb-1">Limited offer</div>
    <div className="flex items-baseline gap-1">
      <span className="text-2xl font-extrabold text-gray-900">−30%</span>
      <span className="text-[11px] text-gray-600">first 7 days</span>
    </div>
    <div className="mt-1 inline-block bg-white border border-dashed border-amber-400 px-2 py-0.5 rounded font-mono text-[10px] font-bold text-amber-700">
      WELCOME30
    </div>
  </div>
);

/* Open Bidding — anonymised competing-offer banner */
export const OpenBiddingMockup = ({ market }) => {
  const c = marketMockupCopy(market);
  return (
    <div className="w-full max-w-[250px] mx-auto rounded-lg bg-gradient-to-br from-amber-50 to-amber-100/60 ring-1 ring-amber-300 p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
        <Gavel className="h-3 w-3" />
        Open bidding active
      </div>
      <div className="mt-1 text-[11.5px] text-amber-900 leading-snug">
        Current highest <strong>{c.fmt(482000)}</strong> · 3 active bidders.
      </div>
      <div className="mt-1.5 flex gap-1.5">
        <button className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-600 text-white">
          Counter {c.fmtShort(490000)}
        </button>
        <button className="text-[10px] font-semibold px-2 py-0.5 rounded bg-white ring-1 ring-amber-300 text-amber-800">Hold</button>
      </div>
    </div>
  );
};
